import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, ArrowLeft, AlertCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import '../styles/SignIn.css';

const RESEND_COOLDOWN_SECONDS = 30;

const VerifyOtp = () => {
  const location = useLocation();
  const navigate = useNavigate();
  console.log('>>> VerifyOtp rendered, location.state =', location.state);
  const { verifySignupOtp, verifySigninOtp, resendOtp, isLoading, error: authError, clearError } = useAuth();
  const { success, error: showError } = useToast();

  const [purpose] = useState(location.state?.purpose);
  const [email] = useState(location.state?.email);
  const [challengeToken, setChallengeToken] = useState(location.state?.challengeToken);

  const [code, setCode] = useState('');
  const [localError, setLocalError] = useState('');
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (!purpose || !challengeToken) {
      navigate('/signin', { replace: true });
    }
  }, [purpose, challengeToken, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleCodeChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(digitsOnly);
    if (localError) setLocalError('');
    if (authError) clearError();
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setLocalError('');
  clearError();

  if (code.length !== 6) {
    setLocalError('Enter the 6-digit code');
    return;
  }

  try {
    const response =
      purpose === 'signup'
        ? await verifySignupOtp(challengeToken, code)
        : await verifySigninOtp(challengeToken, code);

    if (response && response.success) {
      success(purpose === 'signup' ? '✅ Account verified!' : '✅ Welcome back!');
      const loggedInUser = JSON.parse(localStorage.getItem('user') || '{}');
      const destination = loggedInUser.userType === 'platform' ? '/platform/organisations' : '/dashboard';
      setTimeout(() => navigate(destination, { replace: true }), 800);
    }
  } catch (err) {
    const errorMsg = authError || err?.message || 'Verification failed. Please try again.';
    setLocalError(errorMsg);
    showError(errorMsg);
  }
};

  const handleResend = useCallback(async () => {
    if (cooldown > 0) return;
    try {
      const response = await resendOtp(challengeToken);
      setChallengeToken(response.data.challengeToken);
      setCode('');
      setCooldown(RESEND_COOLDOWN_SECONDS);
      success('A new code has been sent');
    } catch (err) {
      showError(err?.message || 'Could not resend code');
    }
  }, [challengeToken, cooldown, resendOtp, success, showError]);

  if (!purpose || !challengeToken) return null;

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
  };

  return (
    <motion.div
      className="signin-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="signin-container" style={{ gridTemplateColumns: '1fr', justifyItems: 'center' }}>
        <motion.div
          className="signin-form-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <motion.form className="signin-form" onSubmit={handleSubmit} style={{ maxWidth: '440px' }}>
            <motion.div variants={itemVariants} initial="hidden" animate="visible" style={{ marginBottom: '1rem' }}>
              <Link to="/signin" className="back-link" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem', color: '#6b7280', textDecoration: 'none' }}>
                <ArrowLeft size={16} />
                Back to Sign In
              </Link>
            </motion.div>

            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
              <ShieldCheck size={40} color="var(--primary, #0ea5e9)" />
            </div>

            <h2 style={{ textAlign: 'center' }}>
              {purpose === 'signup' ? 'Verify Your Email' : 'Two-Step Verification'}
            </h2>
            <p className="form-subtitle" style={{ textAlign: 'center' }}>
              We've sent a 6-digit code to <strong>{email}</strong>
            </p>

            {(localError || authError) && (
              <div className="error-alert">
                <AlertCircle size={18} />
                <span>{localError || authError}</span>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="code">Verification Code</label>
              <div className="input-wrapper">
                <input
                  type="text"
                  inputMode="numeric"
                  id="code"
                  name="code"
                  value={code}
                  onChange={handleCodeChange}
                  placeholder="123456"
                  maxLength={6}
                  disabled={isLoading}
                  autoFocus
                  style={{
                    paddingLeft: '1rem',
                    textAlign: 'center',
                    fontSize: '1.5rem',
                    letterSpacing: '0.5rem',
                    fontWeight: 700,
                  }}
                  required
                />
              </div>
            </div>

            <motion.button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={isLoading || code.length !== 6}
              whileHover={!isLoading ? { scale: 1.02 } : {}}
              whileTap={!isLoading ? { scale: 0.98 } : {}}
            >
              {isLoading ? 'Verifying...' : 'Verify'}
            </motion.button>

            <p className="signup-prompt">
              Didn't get a code?{' '}
              <button
                type="button"
                onClick={handleResend}
                disabled={cooldown > 0}
                className="signup-link"
                style={{ background: 'none', border: 'none', cursor: cooldown > 0 ? 'not-allowed' : 'pointer' }}
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
              </button>
            </p>
          </motion.form>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default VerifyOtp;